/* public-path Sets the cdn path of the chunks.  Must come first in this file */
import '~/common/public-path';

/* Global imports */
import {lazy, Suspense, useEffect, useRef} from 'react';
import {createRoot} from 'react-dom/client';
import {Route, Router, Switch} from 'react-router-dom';
import {Provider, useDispatch, useSelector} from 'react-redux';
import {toast, ToastContainer} from 'react-toastify';
import {ModalProvider} from 'react-modal-hook';
import {TransitionGroup} from 'react-transition-group';

/* Local imports */
import urls from 'urls';
import {SuspenseFallback} from '~library/molecules/SuspenseFallback';
import {history} from './history';
import {LoginModalReduxWrapper} from './components/LoginModal/LoginModalReduxWrapper';
import {store} from './reducers/store';
import {useTrackPages} from './utils/trackPages';
import {NavHeader} from './components/NavHeader/NavHeader';
import {EventCreateDesignContainer} from './routes/EventCreateDesign/EventCreateDesign';
import {
  getCurrentStep,
  useCurrentRoute,
  setupWebviewNavigation,
  navigateToRoute,
} from './navigation';
import {PROPAGATE_STEP_CHANGE, SET_ALL_ASSETS} from './reducers/constants';
import {INIT_APP} from '~/common/fabric/constants';
import {NotifyGuestsOverlay} from './components/modals/NotifyGuestsOverlay';
import {
  setupWebviewCheckoutCompleteConnection,
  setupWebviewDowngradeConnection,
  setupWebviewPostLoginSuccessConnection,
  setupWebviewSendCompleteConnection,
  setupWebviewUpsellAcceptConnection,
  webViewOnNav,
  webViewPendingUpgradeToPremium,
} from './webview/webview';
import {isFabricWebview} from '~/common/utils/webview';
import {fetchInvitationSummary} from '~/ViewInvitation/reducers/apiActions';
import {InjectableFooter} from '~library/organisms/InjectableFooter';
import {eventInEditMode, eventInUpsellMode} from './reducers/details/selectors';
import * as guestsAPI from '~/common/fabric/EventGuests/api/guests';
import {UPDATE_GUESTS} from '~/common/fabric/EventGuests/reducers/guests/constants';
import {isProOrPartyPassUser} from './utils/experiments';
import {getUserData} from '~library/utils/auth/utils';
import {EventReviewWrapper} from '~/common/fabric/routes/EventReview/EventReviewWrapper';
import {setEventToBackendSnapshot} from '~/common/fabric/Snapshot/reducers';
import {stateToBackendEventRepresentation} from '~/CreateFabricInvitation/api/event';

import 'react-toastify/dist/ReactToastify.min.css';
import './app.sass';

const Content = lazy(() =>
  import('./components/Content/Content').then((module) => ({
    default: module.Content,
  }))
);
const EventDetails = lazy(() =>
  import('./routes/EventDetails/EventDetails').then((module) => ({default: module.EventDetails}))
);
const EventGiftingABTest = lazy(() =>
  import('./routes/EventGifting/EventGiftingABTest').then((module) => ({
    default: module.EventGiftingABTest,
  }))
);
const EventGuests = lazy(() =>
  import('~/common/fabric/EventGuests/EventGuests').then((module) => ({
    default: module.EventGuests,
  }))
);
const Success = lazy(() =>
  import('./routes/Success/Success').then((module) => ({default: module.Success}))
);
const Upsell = lazy(() =>
  import('./routes/Upsell/Upsell').then((module) => ({default: module.Upsell}))
);
const InvitationMakerContainer = lazy(() =>
  import('~/CreateFabricInvitation/routes/InvitationMaker/EventCreateDesign').then((module) => ({
    default: module.EventCreateDesignContainer,
  }))
);
const GuestOptions = lazy(() =>
  import('./routes/GuestOptions/GuestOptions').then((module) => ({default: module.GuestOptions}))
);

const isInWebview = isFabricWebview();

const currentUrl = new window.URL(window.location.href);
store.dispatch({
  type: INIT_APP,
  payload: {
    event: window.evite_event,
    template: window.fabricTemplate,
    stamps: [],
    stickers: [],
    liners: [],
    envelopes: [],
    backgrounds: [],
    user: getUserData(isInWebview),
    category_event_type: window.category_event_type,
    category_id: new URLSearchParams(window.location.search).get('c'),
    override_freemium:
      !(isInWebview || isProOrPartyPassUser(window?.subscriptionPlanName)) &&
      currentUrl.searchParams.get('variant') === 'premium',
    widgets: {...window.event_widgets},
  },
});

if (window.fabricTemplate?.premium) {
  webViewPendingUpgradeToPremium(false);
}

if (window.evite_event) {
  store.dispatch(fetchInvitationSummary(window.evite_event.id));
  store.dispatch(guestsAPI.fetchGuestsListThunk()); // duplicated because the AddGuests ABTest
}

const {id: currentStep} = getCurrentStep({store});

if (currentStep >= 5) {
  const {id: eventId} = window.evite_event;

  store.dispatch({type: UPDATE_GUESTS, payload: {eventId}});
  store.dispatch(guestsAPI.fetchContactsThunk());
  store.dispatch(guestsAPI.fetchGuestsListThunk());
  store.dispatch(guestsAPI.fetchGuestsImportThunk());
}

setupWebviewNavigation(store);
setupWebviewDowngradeConnection(store);
setupWebviewCheckoutCompleteConnection(store);
setupWebviewSendCompleteConnection(store);
setupWebviewPostLoginSuccessConnection(store, getCurrentStep, navigateToRoute);
setupWebviewUpsellAcceptConnection(store);

const TrackRouteChanges = ({contentPanel}) => {
  const pathObj = useCurrentRoute();
  const sections = document.getElementsByClassName('create-flow__content-section');

  for (let i = 0; i < sections.length; i++) {
    sections[i].classList.remove('active');
  }

  const activeSection = document.getElementById(pathObj.name);
  if (activeSection && contentPanel?.current) {
    activeSection.classList.add('active');
    // eslint-disable-next-line no-param-reassign
    contentPanel.current.scrollTop = activeSection.offsetTop - 120;
  }

  // Report navigation status back the web apps
  // We do it here to support browsers forward and back buttons as well as in app navigation
  webViewOnNav(pathObj.name, pathObj.nextRoute, pathObj.previousRoute, pathObj.nextButtonText);

  // Track GA page views
  useTrackPages();

  return null;
};

const CONTENT_PATHS = ['/:template/details', '/:template/gifting'];

const FabricCreateInvitationApp = () => {
  const contentPanel = useRef(null);
  const {path, hideInWebView, showFooter} = useCurrentRoute();
  const inEditMode = useSelector(eventInEditMode);
  const inUpsellMode = useSelector(eventInUpsellMode);
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      ['create', 'customize', 'details', 'gifting', 'preview', 'guests', 'review'].includes(path) &&
      !inUpsellMode
    ) {
      document.body.classList.add('no-overflow');
    }

    store.dispatch({type: PROPAGATE_STEP_CHANGE});

    return () => {
      window.scroll(0, 0);
      document.body.classList.remove('no-overflow');
    };
  }, [path]);

  useEffect(() => {
    const appHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };

    window.addEventListener('resize', appHeight);

    appHeight();

    return () => {
      window.removeEventListener('resize', appHeight);
    };
  }, []);

  const sending = path === 'send';

  useEffect(() => {
    const fetchAllAssets = async () => {
      const url = urls.get('ajax_get_all_assets', {
        asset_groups: window.fabricTemplate.asset_groups,
        primary_categories: window.fabricTemplate.primary_categories,
        template: window.fabricTemplate.id,
      });
      const resp = await evite.fetch(url, {
        method: 'get',
        credentials: 'include',
        headers: {Accept: 'application/json'},
      });

      const response = await resp.json();
      const {
        stamps: allStamps,
        stickers: allStickers,
        liners: allLiners,
        backgrounds: allBackgrounds,
        overlays: allOverlays,
        reveals: allReveals,
      } = response;
      const allEnvelopes = response.envelopes
        ? response.envelopes.filter(({blank}) => !!blank)
        : [];
      dispatch({
        type: SET_ALL_ASSETS,
        payload: {
          allAssets: {
            allStamps,
            allStickers,
            allLiners,
            allBackgrounds,
            allEnvelopes,
            allOverlays,
            allReveals,
          },
        },
      });
    };
    fetchAllAssets().then(() => {
      store.dispatch(
        setEventToBackendSnapshot(stateToBackendEventRepresentation(store.getState()))
      );
    });
  }, []);

  const renderGuestOptions = () => <GuestOptions fullscreen />;

  return (
    <>
      <TrackRouteChanges contentPanel={contentPanel} />
      {(path !== 'preview' || !inEditMode) && !sending && <NavHeader />}
      <div className={`root-page-wrapper ${path}`}>
        <div className="create-flow__page" id={path}>
          {(!hideInWebView || !isInWebview) && (
            <Suspense fallback={<SuspenseFallback />}>
              <Switch>
                <Route path={CONTENT_PATHS}>
                  <Content contentPanel={contentPanel} />
                </Route>
              </Switch>
              <Switch>
                <Route
                  exact
                  path={['/:template/create', '/:eventId/customize']}
                  component={EventCreateDesignContainer}
                />
                <Route exact path={['/:eventId/details']} component={EventDetails} />
                <Route exact path={['/:eventId/gifting']} component={EventGiftingABTest} />
                <Route exact path={['/:eventId/preview']} component={InvitationMakerContainer} />
                <Route exact path="/:eventId/guests" component={EventGuests} />
                <Route exact path="/:eventId/guest-options" component={renderGuestOptions} />
                <Route exact path="/:eventId/review" component={EventReviewWrapper} />
                <Route exact path="/:eventId/send" component={Upsell} />
                <Route exact path="/:eventId/confirm" component={Success} />
              </Switch>
            </Suspense>
          )}
        </div>
      </div>
      {showFooter && <InjectableFooter />}
    </>
  );
};

const FabricCreateInvitationAppWrapper = () => (
  <Provider store={store}>
    <ModalProvider rootComponent={TransitionGroup}>
      <Router history={history}>
        <LoginModalReduxWrapper />
        <FabricCreateInvitationApp />
      </Router>
      <ToastContainer
        position="bottom-right"
        autoClose={6000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        icon={false}
      />
      <NotifyGuestsOverlay />
    </ModalProvider>
  </Provider>
);

window.toast = toast; // TODO: Remove if toasts irrelevant here.
const root = createRoot(document.getElementById('fabric-create-invitation-root'));
root.render(<FabricCreateInvitationAppWrapper />);
